import {
  useRef,
  useLayoutEffect,
  useCallback,
  useEffect,
} from "react";

interface WaveCanvasProps {
  peaks: number[];
  height: number;
  waveColor: string;
  progressColor: string;
  barWidth: number;
  progress: number; // 0..1
  id?: string; // for deterministic random generation
  onSeek: (percent: number) => void;
  onPreviewSeek?: (percent: number) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  isDragging: boolean;
}

export const WaveCanvas: React.FC<WaveCanvasProps> = ({
  peaks,
  height,
  waveColor,
  progressColor,
  barWidth,
  progress,
  id,
  onSeek,
  onPreviewSeek,
  onDragStart,
  onDragEnd,
  isDragging,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number | null>(null);
  const pendingSeekPercentRef = useRef<number | null>(null);

  // Canvas مخفی برای موج ثابت
  const waveBufferRef = useRef<HTMLCanvasElement | null>(null);
  const waveMaskBufferRef = useRef<HTMLCanvasElement | null>(null);
  const progressBufferRef = useRef<HTMLCanvasElement | null>(null);
  const lastSizeRef = useRef<{ width: number; height: number } | null>(null);

  /* ================= Utils ================= */

  const calculateProgress = useCallback((clientX: number): number => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;
    const rect = canvas.getBoundingClientRect();
    return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  }, []);

  const flushSeek = useCallback(() => {
    if (pendingSeekPercentRef.current === null) return;
    onSeek(pendingSeekPercentRef.current);
    pendingSeekPercentRef.current = null;
    rafRef.current = null;
  }, [onSeek]);

  const scheduleSeek = useCallback((percent: number) => {
    pendingSeekPercentRef.current = percent;
    if (rafRef.current !== null) return;
    rafRef.current = window.requestAnimationFrame(flushSeek);
  }, [flushSeek]);

  useEffect(() => {
    return () => {
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  const drawRoundedBar = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
  ) => {
    if (width <= 0 || height <= 0) return;
    const barWidth = Math.max(2, width);
    const radius = barWidth / 2;
    const barHeight = Math.max(barWidth, height);
    const left = x;
    const top = y;
    const right = left + barWidth;
    const bottom = top + barHeight;
    const centerX = left + radius;

    ctx.beginPath();
    if (barHeight <= barWidth + 0.001) {
      ctx.arc(centerX, top + barHeight / 2, Math.max(1, barHeight / 2), 0, Math.PI * 2);
    } else {
      ctx.moveTo(left, top + radius);
      ctx.arc(centerX, top + radius, radius, Math.PI, 0, false);
      ctx.lineTo(right, bottom - radius);
      ctx.arc(centerX, bottom - radius, radius, 0, Math.PI, false);
      ctx.closePath();
    }
    ctx.fill();
  };

  const getSmoothedPeak = useCallback((index: number): number => {
    const prev = peaks[Math.max(0, index - 1)] ?? 0;
    const current = peaks[index] ?? 0;
    const next = peaks[Math.min(peaks.length - 1, index + 1)] ?? 0;
    return (prev + current * 2 + next) / 4;
  }, [peaks]);

  /* ============ Static Wave (once) ============ */

  const drawStaticWave = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || peaks.length === 0) return;

    const width = canvas.parentElement?.clientWidth ?? 400;
    const dpr = window.devicePixelRatio || 1;

    // Reuse buffer (avoid re-allocations / memory churn)
    let buffer = waveBufferRef.current;
    if (!buffer) {
      buffer = document.createElement("canvas");
      waveBufferRef.current = buffer;
    }
    let maskBuffer = waveMaskBufferRef.current;
    if (!maskBuffer) {
      maskBuffer = document.createElement("canvas");
      waveMaskBufferRef.current = maskBuffer;
    }
    const scaledWidth = width * dpr;
    const scaledHeight = height * dpr;
    if (buffer.width !== scaledWidth || buffer.height !== scaledHeight) {
      buffer.width = scaledWidth;
      buffer.height = scaledHeight;
    }
    if (maskBuffer.width !== scaledWidth || maskBuffer.height !== scaledHeight) {
      maskBuffer.width = scaledWidth;
      maskBuffer.height = scaledHeight;
    }

    const bctx = buffer.getContext("2d");
    const mctx = maskBuffer.getContext("2d");
    if (!bctx || !mctx) return;

    bctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    bctx.clearRect(0, 0, width, height);
    mctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    mctx.clearRect(0, 0, width, height);

    const totalBars = peaks.length;

    // عرض بار بر اساس تعداد بارها محاسبه میشه
    // هرچه تعداد کمتر، بارها پهن‌تر میشن تا ظاهر موجی داشته باشن
    const idealBarWidth = Math.min(barWidth * 5.2, Math.max(barWidth + 0.55, width / totalBars * 0.66));
    const gap = Math.max(1.2, idealBarWidth * 0.2);
    const spacing = idealBarWidth + gap;
    const totalWaveWidth = totalBars * spacing - gap;

    // همیشه اسکیل می‌کنیم تا کل عرض پر بشه
    const scale = width / totalWaveWidth;

    bctx.fillStyle = waveColor;
    bctx.globalAlpha = 0.78;
    mctx.fillStyle = "#ffffff";
    mctx.globalAlpha = 1;

    const rand = (seed: number) => {
      const baseId = id || 'default';
      const combined = `${baseId}_${seed}`;
      let hash = 0;
      for (let i = 0; i < combined.length; i++) {
        const char = combined.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      const x = Math.sin(hash * 9999) * 10000;
      return x - Math.floor(x);
    };

    const generateOrganicBar = (
      peak: number,
      index: number,
      canvasHeight: number
    ) => {
      const smoothPeak = Math.max(0.03, Math.min(1, getSmoothedPeak(index) * 0.9 + peak * 0.1));
      const envelope = 0.86 + (Math.sin((index / totalBars) * Math.PI * 2) + 1) * 0.07;
      const base = Math.pow(smoothPeak, 0.62) * canvasHeight * envelope;
      const jitter = rand(index + 7) * canvasHeight * 0.08;
      const h = Math.max(4.4, Math.min(canvasHeight * 0.92, base + jitter));
      const y = (canvasHeight - h) / 2;
      return { h, y };
    };

    for (let i = 0; i < totalBars; i++) {
      const { h, y } = generateOrganicBar(
        peaks[i],
        i,
        height
      );

      const x = i * spacing * scale;
      const scaledBw = idealBarWidth * scale;
      drawRoundedBar(bctx, x, y, scaledBw, h);
      drawRoundedBar(mctx, x, y, scaledBw, h);
    }
    bctx.globalAlpha = 1;
    mctx.globalAlpha = 1;

    // ⬅⬅⬅ مهم: رسم buffer روی canvas اصلی
    if (canvas.width !== scaledWidth || canvas.height !== scaledHeight) {
      canvas.width = scaledWidth;
      canvas.height = scaledHeight;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(buffer, 0, 0, width, height);

    lastSizeRef.current = { width, height };
  }, [peaks, height, barWidth, waveColor, id, getSmoothedPeak]);

  /* ============ Progress (continuous) ============ */

  const drawProgress = useCallback(() => {
    const canvas = canvasRef.current;
    const buffer = waveBufferRef.current;
    const maskBuffer = waveMaskBufferRef.current;
    if (!canvas || !buffer || !maskBuffer) return;

    const width = canvas.clientWidth;
    const dpr = window.devicePixelRatio || 1;

    const scaledWidth = width * dpr;
    const scaledHeight = height * dpr;
    if (canvas.width !== scaledWidth || canvas.height !== scaledHeight) {
      canvas.width = scaledWidth;
      canvas.height = scaledHeight;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    // موج ثابت
    ctx.drawImage(buffer, 0, 0, width, height);

    const progressX = width * progress;
    if (progressX > 0) {
      let progressBuffer = progressBufferRef.current;
      if (!progressBuffer) {
        progressBuffer = document.createElement("canvas");
        progressBufferRef.current = progressBuffer;
      }
      if (progressBuffer.width !== scaledWidth || progressBuffer.height !== scaledHeight) {
        progressBuffer.width = scaledWidth;
        progressBuffer.height = scaledHeight;
      }

      const pctx = progressBuffer.getContext("2d");
      if (pctx) {
        pctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        pctx.clearRect(0, 0, width, height);

        const progressGradient = pctx.createLinearGradient(0, 0, Math.max(progressX, 1), 0);
        progressGradient.addColorStop(0, progressColor);
        progressGradient.addColorStop(1, `${progressColor}B8`);
        pctx.fillStyle = progressGradient;
        pctx.fillRect(0, 0, progressX, height);

        // Mask with waveform so progress paint matches bar geometry exactly.
        pctx.globalCompositeOperation = "destination-in";
        pctx.drawImage(maskBuffer, 0, 0, width, height);
        pctx.globalCompositeOperation = "source-over";

        ctx.drawImage(progressBuffer, 0, 0, width, height);
      }
    }
  }, [progress, progressColor, height]);

  /* ============ Effects ============ */

  useLayoutEffect(() => {
    if (peaks.length > 0) {
      drawStaticWave();
      drawProgress();
    }

    const handleResize = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const width = canvas.parentElement?.clientWidth ?? 400;
      const last = lastSizeRef.current;
      if (!last || last.width !== width || last.height !== height) {
        drawStaticWave();
      }
      drawProgress();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [peaks, drawStaticWave, drawProgress, height]);

  useLayoutEffect(() => {
    drawProgress();
  }, [progress, drawProgress]);

  /* ============ Events ============ */

  const updateSeekFromClientX = useCallback((clientX: number) => {
    const percent = calculateProgress(clientX);
    onPreviewSeek?.(percent);
    scheduleSeek(percent);
  }, [calculateProgress, onPreviewSeek, scheduleSeek]);

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    onDragStart();
    updateSeekFromClientX(e.clientX);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDragging) return;
    e.preventDefault();
    updateSeekFromClientX(e.clientX);
  };

  const endDrag = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    if (!isDragging) return;
    if (pendingSeekPercentRef.current !== null) {
      onSeek(pendingSeekPercentRef.current);
      pendingSeekPercentRef.current = null;
    }
    onDragEnd();
  };

  /* ============ Render ============ */

  return (
    <div className="relative w-full">
      <canvas
        ref={canvasRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className="w-full cursor-grab active:cursor-grabbing select-none rounded-lg"
        style={{ height, touchAction: "none" }}
      />
    </div>
  );
};
